import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import {
  Box,
  Container,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Stack,
  Skeleton,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import GetCookieByName from "../../hooks/GetCookie";
import paymentdashboardservice from "../../services/paymentdashboardservice";
import { useWhatsappInbox, useWhatsappThread, useWhatsappNumbers } from "../../hooks/useWhatsapp";
import {
  formatRelativeTime,
  groupMessagesByDate,
  getNumberColor,
  MessageBubble,
  hasRealName,
  conversationDisplayName,
  formatWaId,
  upsertThreadMessage,
  upsertThreadMessageStatus,
} from "./chatUiHelpers";

// Returns { data, found } instead of just the updated cache — the caller needs to know
// whether this conversation existed on ANY currently-cached inbox page (checked across
// every page/filter combo via setQueriesData) so it can force a refetch when it's brand
// new to every one of them, rather than silently dropping it and waiting for "next
// refetch" that, with no polling configured, might otherwise never come on its own.
function bumpInboxPreview(oldData, conversationId, message) {
  if (!oldData?.conversations) return { data: oldData, found: false };
  const idx = oldData.conversations.findIndex((c) => c.conversationId === conversationId);
  if (idx === -1) return { data: oldData, found: false };
  const updated = { ...oldData.conversations[idx], lastMessageAt: message.sentAt, messages: [message] };
  const rest = oldData.conversations.filter((_, i) => i !== idx);
  return { data: { ...oldData, conversations: [updated, ...rest] }, found: true };
}

function ConversationRow({ conv, isSelected, onSelect }) {
  const color = getNumberColor(conv.ourPhoneNumberId || conv.ourDisplayPhoneNumber);
  const lastMsg = (conv.messages || [])[0];
  const needsReply = lastMsg && !lastMsg.isFromUs;
  const preview = lastMsg ? `${lastMsg.isFromUs ? "You: " : ""}${lastMsg.text || `[${lastMsg.type || "attachment"}]`}` : "No messages yet";

  return (
    <Box
      onClick={() => onSelect(conv.conversationId)}
      sx={{
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderLeft: isSelected ? "3px solid #FF5200" : "3px solid transparent",
        bgcolor: isSelected ? "rgba(255,82,0,0.06)" : "transparent",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: isSelected ? "rgba(255,82,0,0.06)" : "rgba(0,0,0,0.02)" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontWeight: needsReply ? 800 : 700,
            fontSize: 13.5,
            color: "#1C1C2E",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {conversationDisplayName(conv)}
        </Typography>
        {conv.optedOut && <BlockRoundedIcon sx={{ fontSize: 13, color: "#E34C14", flexShrink: 0 }} />}
        <Typography sx={{ fontSize: 10.5, color: "#9CA3AF", flexShrink: 0 }}>{formatRelativeTime(conv.lastMessageAt)}</Typography>
      </Stack>
      {/* The name above already prefers influencer data, but the number itself was never
          shown at all — only skip it here when the name line already IS that same number
          (an unmatched conversation with no WhatsApp profile name on file). */}
      {hasRealName(conv) && (
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#9CA3AF", pl: 2.25, mt: 0.1 }}>
          {formatWaId(conv.customerWaId)}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pl: 2.25, mt: 0.4 }}>
        {needsReply && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#FF5200", flexShrink: 0 }} />}
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontSize: 12,
            color: needsReply ? "#374151" : "#9CA3AF",
            fontWeight: needsReply ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {preview}
        </Typography>
      </Stack>
    </Box>
  );
}

function ThreadComposer({ conversation, onSend, sending }) {
  const [text, setText] = useState("");
  const accentColor = getNumberColor(conversation?.ourPhoneNumberId || conversation?.ourDisplayPhoneNumber);
  const noToken = !conversation?.hasActiveToken;
  const windowClosed = conversation?.messagingWindow === "closed";
  const optedOut = conversation?.optedOut;
  const disabled = noToken || windowClosed || optedOut;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled || sending) return;
    setText("");
    onSend(trimmed);
  };

  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.9)", flexShrink: 0 }}>
      {disabled && (
        <Typography sx={{ px: 2.5, py: 1.5, fontSize: 12, fontFamily: "Quicksand", color: "#9CA3AF", fontStyle: "italic" }}>
          {optedOut
            ? "This person has opted out — sending is disabled to respect that."
            : noToken
            ? "Sending is unavailable — no active WhatsApp number connected."
            : "This creator hasn't messaged in the last 24 hours — WhatsApp only allows a pre-approved template message until they message again."}
        </Typography>
      )}
      {!disabled && (
        <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "18px", fontFamily: "Quicksand", fontSize: 13.5, background: "rgba(0,0,0,0.02)" } }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!text.trim() || sending}
            sx={{
              bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)",
              color: text.trim() ? "#fff" : "#9CA3AF",
              "&:hover": { bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)", opacity: 0.9 },
              width: 38,
              height: 38,
            }}
          >
            {sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}

function ThreadPanel({ conversationId, onSend, sending }) {
  const { data, isLoading } = useWhatsappThread(conversationId);
  const scrollRef = useRef(null);
  const conversation = data?.conversation;
  const accentColor = getNumberColor(conversation?.ourPhoneNumberId || conversation?.ourDisplayPhoneNumber);
  const messages = conversation?.messages || [];
  const grouped = groupMessagesByDate(messages);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversationId, messages.length]);

  if (!conversationId) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13.5 }}>
          Select a conversation to view it
        </Typography>
      </Box>
    );
  }

  if (isLoading || !conversation) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
            <Skeleton variant="rounded" width={`${40 + (i % 3) * 15}%`} height={44} sx={{ borderRadius: i % 2 === 0 ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.85)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: accentColor, boxShadow: `0 0 0 3px ${accentColor}30` }} />
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E" }}>
              {conversationDisplayName(conversation)}
            </Typography>
            {hasRealName(conversation) && (
              <Typography sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#9CA3AF" }}>
                {formatWaId(conversation.customerWaId)}
              </Typography>
            )}
          </Stack>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: accentColor, fontWeight: 700 }}>
            via {conversation.ourDisplayPhoneNumber}
          </Typography>
        </Box>
        {conversation.optedOut && (
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#E34C14", ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            <BlockRoundedIcon sx={{ fontSize: 13 }} /> Opted out
          </Typography>
        )}
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          background: "linear-gradient(145deg, rgba(224,247,232,0.4) 0%, rgba(237,241,255,0.3) 100%)",
        }}
      >
        {Object.entries(grouped).map(([dateLabel, msgs]) => (
          <Box key={dateLabel}>
            <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: "Quicksand", color: "#9CA3AF", px: 1.5, py: 0.4, bgcolor: "rgba(0,0,0,0.04)", borderRadius: 99, letterSpacing: 0.5 }}>
                {dateLabel}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
            </Box>
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.waMessageId || `msg-${dateLabel}-${i}`} message={msg} accentColor={accentColor} />
            ))}
          </Box>
        ))}
      </Box>

      <ThreadComposer conversation={conversation} onSend={onSend} sending={sending} />
    </Box>
  );
}

function WhatsAppInboxPage() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const location = useLocation();
  const socketRef = useRef(null);
  const joinedRoomsRef = useRef(new Set());
  const [page, setPage] = useState(1);
  const [numberFilter, setNumberFilter] = useState("all");
  // Seeded from router state when arriving via a WhatsAppNotificationsListener toast's
  // "View" click (navigate("/whatsapp/inbox", { state: { conversationId } })) — a lazy
  // initializer so it's set before the auto-select effect below ever runs, rather than
  // racing it via a separate effect.
  const [selectedConversationId, setSelectedConversationId] = useState(() => location.state?.conversationId || null);
  const [sending, setSending] = useState(false);

  // Opening the Inbox counts as "seen it" — reset the sidebar badge (see
  // WhatsAppNotificationsListener.jsx, which is what increments this same cache key).
  useEffect(() => {
    queryClient.setQueryData(["whatsapp-unread"], 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: numbersData } = useWhatsappNumbers();
  const numbers = numbersData?.tokens || [];
  const phoneNumberId = numberFilter === "all" ? undefined : numberFilter;
  const { data, isLoading } = useWhatsappInbox(page, phoneNumberId);
  // Memoized so the effects below (auto-select, socket join) only re-run when the actual
  // conversation list changes, not on every render's fresh `|| []` fallback reference.
  const conversations = useMemo(() => data?.conversations || [], [data]);
  const total = data?.total || 0;
  const limit = data?.limit || 30;
  const totalPages = Math.max(Math.ceil(total / limit), 1);

  // Auto-select the first (most recently active) conversation once the list loads, so the
  // inbox never opens on an empty right pane when there's clearly something to look at.
  useEffect(() => {
    if (!selectedConversationId && conversations.length) {
      setSelectedConversationId(conversations[0].conversationId);
    }
  }, [conversations, selectedConversationId]);

  // Live updates — one socket for the whole inbox. Deliberately does NOT depend on
  // `conversations` (or `page`/`numberFilter`) — the handlers below write straight into the
  // whatsapp-inbox/whatsapp-thread query caches, which is exactly what re-deriving
  // `conversations` from would do, so depending on it here made this effect tear down and
  // reconnect the socket after every single incoming message (new data -> effect re-runs ->
  // socket disconnects -> reconnects -> rooms rejoined from scratch) — whatever arrived
  // during that reconnect gap was silently dropped. setQueriesData below updates whichever
  // inbox page/filter is currently cached without needing page/numberFilter as dependencies;
  // room joins are handled by the separate effect below, on the existing socket.
  useEffect(() => {
    let authToken;
    try {
      authToken = JSON.parse(GetCookieByName("auth"))["token"];
    } catch (e) {
      return undefined;
    }

    const socket = io(new URL(process.env.REACT_APP_API_URL).origin, { auth: { token: authToken } });
    socketRef.current = socket;
    joinedRoomsRef.current = new Set();

    // Diagnostic — a "real-time not working" report with no obvious cause in the code means
    // the fastest way to actually find it is to see what the browser's own socket did, not
    // guess further. Safe to leave in: low-noise, and this page is internal-only.
    socket.on("connect", () => console.log("[wa-inbox] socket connected", socket.id));
    socket.on("connect_error", (err) => console.error("[wa-inbox] socket connect_error:", err.message));
    socket.on("disconnect", (reason) => console.warn("[wa-inbox] socket disconnected:", reason));

    socket.on("new-whatsapp-message", ({ conversationId, message }) => {
      console.log("[wa-inbox] new-whatsapp-message received", conversationId, message?.text?.slice(0, 40));
      queryClient.setQueryData(["whatsapp-thread", conversationId], (old) => upsertThreadMessage(old, conversationId, message));

      // Bumping an existing row's preview can't invent a row that doesn't exist yet — a
      // brand-new conversation (first-time replier, or the first send to someone new via
      // a Plan/Campaign) won't be on any currently-cached page. Track whether it was found
      // on ANY of them; if not, force a real refetch instead of silently dropping the
      // event and hoping something else eventually refreshes the list.
      let foundOnAnyPage = false;
      queryClient.setQueriesData(["whatsapp-inbox"], (old) => {
        const { data, found } = bumpInboxPreview(old, conversationId, message);
        if (found) foundOnAnyPage = true;
        return data;
      });
      if (!foundOnAnyPage) queryClient.invalidateQueries(["whatsapp-inbox"]);
    });

    socket.on("whatsapp-message-status", ({ conversationId, waMessageId, status }) => {
      queryClient.setQueryData(["whatsapp-thread", conversationId], (old) => upsertThreadMessageStatus(old, conversationId, waMessageId, status));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient]);

  // Joins any conversation rooms not already joined on the current socket — runs whenever
  // the visible list changes (page/filter change, new thread appearing), without touching
  // the socket connection the effect above owns.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const toJoin = conversations.map((c) => c.conversationId).filter((id) => id && !joinedRoomsRef.current.has(id));
    if (!toJoin.length) return;

    const doJoin = () => {
      toJoin.forEach((id) => {
        socket.emit("join", id);
        joinedRoomsRef.current.add(id);
      });
    };
    if (socket.connected) doJoin();
    else socket.once("connect", doJoin);
  }, [conversations]);

  const handleSend = async (text) => {
    setSending(true);
    try {
      const res = await paymentdashboardservice.sendCreatorWhatsAppMessage(selectedConversationId, text);
      if (!res?.success) throw new Error(res?.message || "Failed to send message");
      queryClient.setQueryData(["whatsapp-thread", selectedConversationId], (old) => upsertThreadMessage(old, selectedConversationId, res.message));
      // selectedConversationId is always one already open/selected from the loaded list,
      // so it's always found here — no need for the not-found handling the live socket
      // event's own call site (above) needs for a genuinely brand-new conversation.
      queryClient.setQueryData(["whatsapp-inbox", page, numberFilter], (old) => bumpInboxPreview(old, selectedConversationId, res.message).data);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to send message", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
            <WhatsAppIcon sx={{ color: "#25D366" }} /> WhatsApp Inbox
          </Typography>
          {numbers.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={numberFilter}
                onChange={(e) => {
                  setNumberFilter(e.target.value);
                  setPage(1);
                  setSelectedConversationId(null);
                }}
                displayEmpty
              >
                <MenuItem value="all">All numbers</MenuItem>
                {numbers.map((n) => (
                  <MenuItem key={n.wabaId} value={n.phoneNumberId}>
                    {n.displayPhoneNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            border: "1px solid rgba(0,0,0,0.07)",
            borderRadius: "16px",
            overflow: "hidden",
            height: "calc(100vh - 260px)",
            minHeight: 520,
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          {/* Conversation list */}
          <Box sx={{ width: { xs: "100%", sm: 300, md: 340 }, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={54} sx={{ mb: 1, borderRadius: "10px" }} />
                  ))}
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                    No conversations yet — once someone messages one of your WhatsApp numbers, it'll show up here.
                  </Typography>
                </Box>
              ) : (
                conversations.map((conv) => (
                  <ConversationRow
                    key={conv.conversationId}
                    conv={conv}
                    isSelected={conv.conversationId === selectedConversationId}
                    onSelect={setSelectedConversationId}
                  />
                ))
              )}
            </Box>
            {totalPages > 1 && (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 12, fontFamily: "Quicksand", color: "#9CA3AF" }}>
                  {page} / {totalPages}
                </Typography>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>

          <ThreadPanel conversationId={selectedConversationId} onSend={handleSend} sending={sending} />
        </Box>
      </Container>
    </Box>
  );
}

export default WhatsAppInboxPage;
